import moment from 'moment';

/**
 * Determines when the checkered flag goes out. Mirrors Moto.Domain.Model.FinishMode.
 *
 * Both modes work the same way: a competitor finishes on their first crossing of the
 * line at or after the finish gate. Only the gate differs.
 */
export const FinishMode = {
	/** The flag only goes out when the leader completes his lap. The default - see the C# enum. */
	OnLeaderFinish: 0,
	/** The flag is out the moment the race clock expires. */
	OnTimeExpiry: 1
};

export interface RaceFinishConfig {
	/** Race length in minutes. 0 means the race is decided on laps alone. */
	duration?: number;
	/** Extra laps the leader runs after the clock expires, or the total lap count when duration is 0. */
	laps?: number;
	finishMode?: number;
}

const liveLaps = (result: any): any[] => result.checkpoints.filter((c: any) => !c.deleted);

const cfg = (race: RaceFinishConfig | null | undefined) => ({
	duration: Number(race && race.duration) || 0,
	laps: Number(race && race.laps) || 0,
	mode: Number(race && race.finishMode) || 0
});

/**
 * The finish gate: the instant the checkered flag goes out.
 *
 *   OnTimeExpiry   - the clock expiring, known as soon as the race starts.
 *   OnLeaderFinish - the leader completing his lap, not known until he comes round.
 *
 * Resolved from the *unfiltered* checkpoint list, because the flag is a property of the
 * race and not of whichever class the viewer happens to be filtering on.
 *
 * Returns null for a laps-only race (no gate - see applyFinishState) and while the flag
 * has yet to drop.
 */
export function resolveFinishGate(
	checkpoints: any[],
	competitors: any,
	race: RaceFinishConfig | null | undefined,
	racestart: moment.Moment
): moment.Moment | null {
	const { duration, laps, mode } = cfg(race);

	if (duration <= 0) return null;

	const expiresAt = moment(racestart).add(duration, 'minutes');
	if (mode === FinishMode.OnTimeExpiry) return expiresAt;

	if (!checkpoints || !competitors) return null;

	// Every competitor's passes, in time order.
	const byCompetitor = new Map<string, moment.Moment[]>();
	for (const cp of checkpoints) {
		if (cp.deleted || !cp.competitorId || !competitors[cp.competitorId]) continue;
		let passes = byCompetitor.get(cp.competitorId);
		if (!passes) {
			passes = [];
			byCompetitor.set(cp.competitorId, passes);
		}
		passes.push(moment(cp.timestamp));
	}

	const field = Array.from(byCompetitor.values());
	field.forEach(passes => passes.sort((a, b) => a.valueOf() - b.valueOf()));

	// Laps the leader had completed when the clock expired.
	const leaderLaps = field.reduce((most, passes) => Math.max(most, passes.filter(t => !t.isAfter(expiresAt)).length), 0);
	if (!leaderLaps) return null; // nobody completed a lap before the clock expired

	// The flag falls when the first competitor completes that lap plus the extra laps.
	// Whoever gets there first IS the leader, so there is no need to work out who he is.
	// Note this is the earliest time anyone reaches an *absolute* lap number - not the
	// earliest of each rider's own "+N laps from their own crossing", which a rider a lap
	// down would reach sooner and so flag the field off a backmarker.
	const flagLap = leaderLaps + laps + 1;
	const reached = field.map(passes => passes[flagLap - 1]).filter(Boolean);
	return reached.length ? moment.min(reached) : null; // leader still out on track
}

/**
 * Marks each competitor's finishing pass, and any passes made after it (cool-down laps
 * over the mat, which must not count as race laps).
 */
export function applyFinishState(
	results: any[],
	race: RaceFinishConfig | null | undefined,
	racestart: moment.Moment,
	gate: moment.Moment | null
): void {
	// calculateReults re-runs from scratch on every passing and mutates the shared
	// checkpoint objects, so stale flags from the previous run must be cleared first.
	results.forEach(result => {
		result.finished = false;
		result.finishPass = null;
		result.finishTime = null;
		result.checkpoints.forEach((c: any) => {
			c.isFinish = false;
			c.afterFinish = false;
		});
	});

	if (!race || !results.length) return;

	const { duration, laps } = cfg(race);
	let finishPassFor: (result: any) => any;

	if (duration <= 0) {
		// Laps-only. There is no gate - everyone simply runs the same distance, so a
		// competitor finishes on their own Nth lap whenever they get there.
		if (laps <= 0) return; // unbounded race, nothing to mark
		finishPassFor = result => {
			const passes = liveLaps(result);
			return passes.length >= laps ? passes[laps - 1] : null;
		};
	} else {
		if (!gate) return; // flag has not dropped yet
		finishPassFor = result => liveLaps(result).find(c => !c.timestamp.isBefore(gate)) || null;
	}

	results.forEach(result => {
		const pass = finishPassFor(result);
		if (!pass) return;

		pass.isFinish = true;
		result.finished = true;
		result.finishPass = pass;
		result.finishTime = pass.totaltime;

		const passes = liveLaps(result);
		for (let i = passes.indexOf(pass) + 1; i < passes.length; i++) {
			passes[i].afterFinish = true;
		}
	});
}

export interface RaceClock {
	/** False for a laps-only or unbounded race - there is no countdown to show. */
	hasClock: boolean;
	expiresAt: moment.Moment | null;
	/** Milliseconds until the clock expires. Negative once it has. */
	remaining: number;
	expired: boolean;
	/** True once the checkered flag is actually out. */
	flagOut: boolean;
	/** Extra laps the leader runs after the clock expires. */
	extraLaps: number;
	/** Total laps, for a laps-only race. 0 otherwise. */
	totalLaps: number;
}

/** Describes where the race is against the clock, for the live header. */
export function getRaceClock(
	race: RaceFinishConfig | null | undefined,
	racestart: moment.Moment,
	now: moment.Moment,
	gate: moment.Moment | null
): RaceClock {
	const { duration, laps, mode } = cfg(race);

	if (duration <= 0) {
		return { hasClock: false, expiresAt: null, remaining: 0, expired: false, flagOut: false, extraLaps: 0, totalLaps: laps };
	}

	const expiresAt = moment(racestart).add(duration, 'minutes');

	return {
		hasClock: true,
		expiresAt,
		remaining: expiresAt.diff(now),
		expired: !now.isBefore(expiresAt),
		// The gate already encodes the mode, so this needs no branch on it.
		flagOut: gate != null && !now.isBefore(gate),
		extraLaps: mode === FinishMode.OnLeaderFinish ? laps : 0,
		totalLaps: 0
	};
}
